// SPDX-License-Identifier: AGPL-3.0-or-later

import * as VoiceCallLayoutCommands from '@app/features/voice/commands/VoiceCallLayoutCommands';
import {getStreamKey, parseStreamKey} from '@app/features/voice/components/StreamKeys';
import {parseVoiceParticipantIdentity} from '@app/features/voice/components/VoiceParticipantSpeakingUtils';
import {syncLocalVoiceStateWithServer} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import {
	createVoiceStreamWatchSnapshot,
	transitionVoiceStreamWatchSnapshot,
	type VoiceStreamWatchEvent,
} from '@app/features/voice/engine/VoiceStreamWatchStateMachine';
import {VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import VoiceCallLayout from '@app/features/voice/state/VoiceCallLayout';
import {ME} from '@fluxer/constants/src/AppConstants';

interface StopWatchingStreamKeyOptions {
	guildId?: string | null;
	channelId?: string | null;
	clearPinned?: boolean;
	sync?: boolean;
}

const PUBLISHER_REPUBLISH_GRACE_MS = 4000;
const pendingStopByStreamKey = new Map<
	string,
	{
		timer: NodeJS.Timeout;
		options: StopWatchingStreamKeyOptions;
	}
>();

interface PruneInactiveWatchedStreamsOptions {
	guildId?: string | null;
	channelId: string;
	isStreamActive: (connectionId: string) => boolean;
}

export interface WatchTransitionResult {
	previousKeys: Array<string>;
	keys: Array<string>;
	addedKeys: Array<string>;
	removedKeys: Array<string>;
	changed: boolean;
	membershipChanged: boolean;
	hadStreams: boolean;
	hasStreams: boolean;
	deferredStopKeys: ReadonlySet<string>;
}

export function normalizeStreamGuildId(guildId: string | null | undefined): string | null {
	if (!guildId || guildId === ME) return null;
	return guildId;
}

export function getStreamKeyForParticipantIdentity(
	guildId: string | null | undefined,
	channelId: string | null | undefined,
	participantIdentity: string,
): string | null {
	const {connectionId} = parseVoiceParticipantIdentity(participantIdentity);
	if (!connectionId || !channelId) return null;
	return getStreamKey(normalizeStreamGuildId(guildId), channelId, connectionId);
}

function getPinnedScreenShareStreamKey(
	guildId: string | null | undefined,
	channelId: string | null | undefined,
): string | null {
	if (!channelId) return null;
	if (VoiceCallLayout.pinnedParticipantSource !== VoiceTrackSource.ScreenShare) return null;
	const pinnedIdentity = VoiceCallLayout.pinnedParticipantIdentity;
	if (!pinnedIdentity) return null;
	return getStreamKeyForParticipantIdentity(guildId, channelId, pinnedIdentity);
}

function clearPinnedScreenShareIfMatches(
	streamKey: string,
	guildId: string | null | undefined,
	channelId: string | null | undefined,
): void {
	if (getPinnedScreenShareStreamKey(guildId, channelId) !== streamKey) return;
	VoiceCallLayoutCommands.setPinnedParticipant(null);
}

function arrayDifference(left: ReadonlyArray<string>, right: ReadonlyArray<string>): Array<string> {
	const rightSet = new Set(right);
	return left.filter((key) => !rightSet.has(key));
}

function getPendingStopKeys(): Array<string> {
	return Array.from(pendingStopByStreamKey.keys());
}

function syncPendingStopTimers(nextDeferredStopKeys: ReadonlySet<string>): void {
	for (const [streamKey, pending] of Array.from(pendingStopByStreamKey)) {
		if (nextDeferredStopKeys.has(streamKey)) continue;
		clearTimeout(pending.timer);
		pendingStopByStreamKey.delete(streamKey);
	}
}

function transitionWatchState(event: VoiceStreamWatchEvent): WatchTransitionResult {
	const previousKeys = LocalVoiceState.getViewerStreamKeys();
	const previousSnapshot = createVoiceStreamWatchSnapshot(previousKeys, getPendingStopKeys());
	const nextSnapshot = transitionVoiceStreamWatchSnapshot(previousSnapshot, event);
	const keys = [...nextSnapshot.context.viewerStreamKeys];
	const previousNormalizedKeys = [...previousSnapshot.context.viewerStreamKeys];
	const membershipChanged =
		previousNormalizedKeys.length !== keys.length || previousNormalizedKeys.some((key, index) => key !== keys[index]);
	const changed = previousKeys.length !== keys.length || previousKeys.some((key, index) => key !== keys[index]);
	if (changed) {
		LocalVoiceState.updateViewerStreamKeys(keys);
	}
	syncPendingStopTimers(nextSnapshot.context.deferredStopKeys);
	return {
		previousKeys: previousNormalizedKeys,
		keys,
		addedKeys: arrayDifference(keys, previousNormalizedKeys),
		removedKeys: arrayDifference(previousNormalizedKeys, keys),
		changed,
		membershipChanged,
		hadStreams: previousNormalizedKeys.length > 0,
		hasStreams: keys.length > 0,
		deferredStopKeys: nextSnapshot.context.deferredStopKeys,
	};
}

function syncWatchedKeysIfChanged(result: WatchTransitionResult, sync: boolean): void {
	if (!sync || !result.changed) return;
	syncLocalVoiceStateWithServer({viewer_stream_keys: result.keys});
}

export function replaceWatchedStreamKeys(
	keys: ReadonlyArray<string>,
	options: Pick<StopWatchingStreamKeyOptions, 'sync'> = {},
): WatchTransitionResult {
	const result = transitionWatchState({type: 'watch.replace', keys});
	syncWatchedKeysIfChanged(result, options.sync ?? true);
	return result;
}

export function addWatchedStreamKey(
	streamKey: string,
	options: Pick<StopWatchingStreamKeyOptions, 'sync'> = {},
): WatchTransitionResult {
	const result = transitionWatchState({type: 'watch.add', key: streamKey});
	syncWatchedKeysIfChanged(result, options.sync ?? true);
	return result;
}

export function stopWatchingStreamKey(streamKey: string, options: StopWatchingStreamKeyOptions = {}): boolean {
	const {guildId = null, channelId = null, clearPinned = true, sync = true} = options;
	const result = transitionWatchState({type: 'watch.remove', key: streamKey});
	syncWatchedKeysIfChanged(result, sync);
	if (!result.membershipChanged) return false;
	if (clearPinned) {
		clearPinnedScreenShareIfMatches(streamKey, guildId, channelId);
	}
	return true;
}

export function deferStopWatchingStreamKey(streamKey: string, options: StopWatchingStreamKeyOptions = {}): void {
	const result = transitionWatchState({type: 'watch.deferRemove', key: streamKey});
	if (!result.deferredStopKeys.has(streamKey)) return;
	const existing = pendingStopByStreamKey.get(streamKey);
	if (existing) {
		clearTimeout(existing.timer);
	}
	const timer = setTimeout(() => {
		pendingStopByStreamKey.delete(streamKey);
		stopWatchingStreamKey(streamKey, options);
	}, PUBLISHER_REPUBLISH_GRACE_MS);
	pendingStopByStreamKey.set(streamKey, {timer, options});
}

export function cancelDeferredStopWatchingStreamKey(streamKey: string): boolean {
	const pending = pendingStopByStreamKey.get(streamKey);
	const result = transitionWatchState({type: 'watch.cancelDeferredRemove', key: streamKey});
	return Boolean(pending || result.deferredStopKeys.has(streamKey));
}

export function pruneInactiveWatchedStreamsForChannel({
	guildId,
	channelId,
	isStreamActive,
}: PruneInactiveWatchedStreamsOptions): Array<string> {
	const normalizedGuildId = normalizeStreamGuildId(guildId);
	const current = LocalVoiceState.getViewerStreamKeys();
	if (current.length === 0) return [];
	const removed: Array<string> = [];
	for (const streamKey of current) {
		const parsed = parseStreamKey(streamKey);
		if (!parsed) continue;
		if (parsed.channelId !== channelId || parsed.guildId !== normalizedGuildId) continue;
		if (isStreamActive(parsed.connectionId)) continue;
		removed.push(streamKey);
	}
	if (removed.length === 0) return [];
	const result = transitionWatchState({type: 'watch.removeMany', keys: removed});
	syncWatchedKeysIfChanged(result, true);
	if (!result.membershipChanged) return [];
	for (const streamKey of removed) {
		clearPinnedScreenShareIfMatches(streamKey, normalizedGuildId, channelId);
	}
	return result.removedKeys;
}
